import {
  OperationalStatus,
  SensorType,
} from "@phc/common/gen/threatAnalyticsGen"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

export interface BioMeshFilters {
  sensorTypes: Set<SensorType>
  operationalStatuses: Set<OperationalStatus>
}

/** Returns true when a sensor passes all active filter criteria. */
export function matchesSensorFilters(
  filters: BioMeshFilters,
  sensor: {
    sensor_type: SensorType
    operational_status: OperationalStatus
  }
) {
  return (
    filters.sensorTypes.has(sensor.sensor_type) &&
    filters.operationalStatuses.has(sensor.operational_status)
  )
}

/** Group sensors by platform_id, keeping only those that pass the active filters. */
export function groupSensorsByNode<
  T extends {
    platform_id?: string | null
    sensor_type: SensorType
    operational_status: OperationalStatus
  },
>(
  sensors: T[] | undefined,
  filters: BioMeshFilters,
  nodeIds?: string[]
): Map<string, T[]> {
  const map = new Map<string, T[]>()
  if (!sensors) return map
  for (const s of sensors) {
    if (!s.platform_id) continue
    if (nodeIds && !nodeIds.includes(s.platform_id)) continue
    if (!matchesSensorFilters(filters, s)) continue
    const arr = map.get(s.platform_id)
    if (arr) arr.push(s)
    else map.set(s.platform_id, [s])
  }
  return map
}

interface BioMeshUIStateContextValue {
  filters: BioMeshFilters
  toggleSensorType: (value: SensorType) => void
  toggleOperationalStatus: (value: OperationalStatus) => void
  isEditing: boolean
  setIsEditing: (on: boolean) => void
  isSelecting: boolean
  setIsSelecting: (on: boolean) => void
  selectedNodeIds: Set<string>
  setSelectedNodeIds: (ids: Set<string>) => void
  clearSelection: () => void
}

export const ALL_SENSOR_TYPES = Object.values(SensorType)
export const ALL_OPERATIONAL_STATUSES = Object.values(OperationalStatus)

function toggleInSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

const BioMeshUIStateContext = createContext<BioMeshUIStateContextValue | null>(
  null
)

export function BioMeshUIStateProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [sensorTypes, setSensorTypes] = useState(
    () => new Set<SensorType>(ALL_SENSOR_TYPES)
  )
  const [operationalStatuses, setOperationalStatuses] = useState(
    () => new Set<OperationalStatus>(ALL_OPERATIONAL_STATUSES)
  )
  const [isEditing, _setIsEditing] = useState(false)
  const [isSelecting, _setIsSelecting] = useState(false)
  const [selectedNodeIds, setSelectedNodeIds] = useState(
    () => new Set<string>()
  )

  // Editing and selecting are mutually exclusive
  const setIsEditing = useCallback((on: boolean) => {
    _setIsEditing(on)
    if (on) {
      _setIsSelecting(false)
      setSelectedNodeIds(new Set())
    }
  }, [])

  const setIsSelecting = useCallback((on: boolean) => {
    _setIsSelecting(on)
    if (on) _setIsEditing(false)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedNodeIds(new Set())
  }, [])

  const toggleSensorType = useCallback(
    (v: SensorType) => setSensorTypes(s => toggleInSet(s, v)),
    []
  )
  const toggleOperationalStatus = useCallback(
    (v: OperationalStatus) => setOperationalStatuses(s => toggleInSet(s, v)),
    []
  )

  const filters = useMemo<BioMeshFilters>(
    () => ({ sensorTypes, operationalStatuses }),
    [sensorTypes, operationalStatuses]
  )

  const value = useMemo<BioMeshUIStateContextValue>(
    () => ({
      filters,
      toggleSensorType,
      toggleOperationalStatus,
      isEditing,
      setIsEditing,
      isSelecting,
      setIsSelecting,
      selectedNodeIds,
      setSelectedNodeIds,
      clearSelection,
    }),
    [
      filters,
      toggleSensorType,
      toggleOperationalStatus,
      isEditing,
      setIsEditing,
      isSelecting,
      setIsSelecting,
      selectedNodeIds,
      clearSelection,
    ]
  )

  return (
    <BioMeshUIStateContext.Provider value={value}>
      {children}
    </BioMeshUIStateContext.Provider>
  )
}

export function useBioMeshUIState() {
  const ctx = useContext(BioMeshUIStateContext)
  if (!ctx)
    throw new Error(
      "useBioMeshUIState must be used within BioMeshUIStateProvider"
    )
  return ctx
}
